import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { TaxonomyPicker } from "./TaxonomyPicker";

export function MerchantCategories() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("all");
  const load = useCallback(() => api.get("/admin/merchant/categories").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const save = async (c, body) => {
    try { await api.put(`/admin/merchant/categories/${c.id}`, body); toast.success(`Saved ${c.name}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const applyAll = async () => { const r = await api.post("/admin/merchant/categories/apply-suggestions", {}); toast.success(`${r.data.mapped} categories mapped from suggestions`); load(); };
  if (!rows) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const unmapped = rows.filter((r) => !r.google_id && r.products > 0).length;
  const view = rows.filter((r) => (!q || r.name.toLowerCase().includes(q.toLowerCase()))
    && (only === "all" || (only === "unmapped" ? !r.google_id : !!r.google_id)));
  return (
    <div className="space-y-4" data-testid="gmc-categories">
      <div className="flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter categories" className="w-56" data-testid="gmc-cat-filter" />
        <select value={only} onChange={(e) => setOnly(e.target.value)} className="h-9 rounded-md border px-2 text-sm" data-testid="gmc-cat-only"><option value="all">All</option><option value="unmapped">Unmapped</option><option value="mapped">Mapped</option></select>
        <Button onClick={applyAll} className="bg-steel text-white" data-testid="gmc-cat-apply-all">Apply suggested mappings to unmapped</Button>
        {unmapped > 0 && <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700" data-testid="gmc-cat-unmapped-warning">{unmapped} categories with products are unmapped</span>}
      </div>
      <p className="text-xs text-muted-foreground">IDs come from Google's official product taxonomy file (en-US). Suggestions never overwrite a manual mapping.</p>
      <div className="rounded-xl border bg-white divide-y">
        {view.map((c) => {
          const s = c.suggestions[0];
          return (
            <div key={c.id} className="p-3 grid md:grid-cols-[1.2fr_2fr_auto] gap-3 items-center text-sm" data-testid={`gmc-cat-${c.slug}`}>
              <div><b className="text-steel">{c.name}</b><div className="text-xs text-muted-foreground">{c.products} products{c.source ? ` · ${c.source}` : ""}</div></div>
              <div className="space-y-1">
                {c.google_id ? <div className="text-xs"><b>{c.google_id}</b> · {c.google_path}</div> : <div className="text-xs text-amber-700">Unmapped</div>}
                {!c.google_id && s && <button className="text-xs underline text-steel" onClick={() => save(c, { google_id: s.id, source: "suggested" })} data-testid={`gmc-cat-suggest-${c.slug}`}>Use suggestion: {s.id} · {s.path}</button>}
                <TaxonomyPicker value={c.google_id} onChange={(id) => save(c, { google_id: id, source: "manual" })} testid={`gmc-cat-pick-${c.slug}`} />
              </div>
              <label className="flex items-center gap-2 text-xs whitespace-nowrap"><Switch checked={c.excluded} onCheckedChange={(v) => save(c, { google_id: c.google_id, excluded: v })} data-testid={`gmc-cat-exclude-${c.slug}`} /> Exclude</label>
            </div>);
        })}
      </div>
    </div>
  );
}
